import { useState } from "react"

import { OnboardingDebtScheduleStateEnum } from "@boundless/bl-api-client/customer/models/OnboardingDebtScheduleStateEnum"
import {
  getCustomerCompanyRetrieveQueryKey,
  useCustomerCompanyPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import dayjs from "dayjs"
import { CircleAlertIcon } from "lucide-react"

import { SummaryAccordionItem } from "@/components/summary/SummaryAccordion"
import { AccordionContent } from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { debtsColumns } from "@/lib/data-tables/debt-columns"

import type {
  Company,
  DebtSchedule,
} from "@boundless/bl-api-client/customer/models"

type SummaryDebtTableProps = {
  company: Company
  debts: DebtSchedule[]
  needsReview: boolean
  returnTo: string
}

type DebtConfirmationBannerProps = {
  company: Company
  confirmed: boolean
  onConfirmed: () => void
  returnTo: string
}

/** Asks the borrower to reconfirm a stale Debt Answer. */
function DebtConfirmationBanner({
  company,
  confirmed,
  onConfirmed,
  returnTo,
}: DebtConfirmationBannerProps) {
  const queryClient = useQueryClient()
  const { mutate: updateCompany, isPending } = useCustomerCompanyPartialUpdate({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(
          getCustomerCompanyRetrieveQueryKey(company.uuid),
          data,
        )
        onConfirmed()
      },
    },
  })

  const daysAgo = dayjs().diff(company.debtScheduleConfirmedAt, "day")
  const isSkipped =
    company.onboardingDebtScheduleState ===
    OnboardingDebtScheduleStateEnum.skipped

  return (
    <div className="border-warning-foreground bg-warning mb-4 flex flex-col gap-3 rounded-lg border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      {confirmed ? (
        <p className="font-medium">Debt Table confirmed!</p>
      ) : (
        <>
          <p>
            {isSkipped
              ? "You skipped your debt schedule. Add your debts or confirm you have none."
              : `Last confirmed ${daysAgo} days ago. Is this list still accurate?`}
          </p>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Button
              size="sm"
              disabled={isPending}
              onClick={() =>
                updateCompany({
                  uuid: company.uuid,
                  data: {
                    onboardingDebtScheduleState: isSkipped
                      ? OnboardingDebtScheduleStateEnum["no-debt"]
                      : company.onboardingDebtScheduleState,
                  },
                })
              }
            >
              {isSkipped ? "I have no debt" : "Yes, still accurate"}
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/company/debt-table" search={{ returnTo }}>
                Update
              </Link>
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/** Explains an empty Debt Table by the borrower's Debt Answer. */
function emptyDebtsMessage(company: Company) {
  switch (company.onboardingDebtScheduleState) {
    case OnboardingDebtScheduleStateEnum["no-debt"]:
      return "No debt declared"
    case OnboardingDebtScheduleStateEnum.skipped:
      return "Debt skipped"
    default:
      return "No results."
  }
}

export function SummaryDebtTable({
  company,
  debts,
  needsReview,
  returnTo,
}: SummaryDebtTableProps) {
  const [confirmed, setConfirmed] = useState(false)
  const table = useReactTable({
    data: debts,
    columns: debtsColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const showsBanner = needsReview || confirmed

  return (
    <SummaryAccordionItem
      value="debt-table"
      title="Debt Table"
      badge={
        needsReview && (
          <Badge variant="destructive">
            <CircleAlertIcon />
            Needs review
          </Badge>
        )
      }
    >
      <AccordionContent className="px-4 pt-2 pb-4">
        {showsBanner && (
          <DebtConfirmationBanner
            company={company}
            confirmed={confirmed}
            onConfirmed={() => setConfirmed(true)}
            returnTo={returnTo}
          />
        )}
        <div className="mb-4 flex justify-end">
          <Link
            className="text-primary hover:underline"
            to="/company/debt-table"
            search={{ returnTo }}
          >
            Edit
          </Link>
        </div>
        {debts && (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={debtsColumns.length}
                    className="h-24 text-center"
                  >
                    {emptyDebtsMessage(company)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </AccordionContent>
    </SummaryAccordionItem>
  )
}
